import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueries, useQuery } from 'react-query'
import { ChevronsDownUp, ChevronsUpDown, Minus, Plus, Scan } from 'lucide-react'
import {
  agentService,
  catalogService,
  circleService,
  teamService,
  type CanvasCircle,
  type CanvasRole,
  type CanvasView,
  type CatalogBindingView,
  type RoleSkillChoice,
} from '../services/api'
import { CircleBoard, LinkShape, type CirclePlacement, type PortHit } from '../components/CircleBoard'
import { RoleCatalogResources } from '../components/TeamResourceBoard'
import { CircleRoleSummary } from '../components/CircleRoleSummary'
import { SkillPicker } from '../components/SkillPicker'
import { trackPointer } from '../lib/trackPointer'
import { circleModelPath } from '../lib/teamLinks'
import {
  circleRadius,
  separateCircles,
  isExternal,
  isInput,
  isOutput,
  linkCrossesRole,
  portAnchor,
  spreadCircleOrigins,
  type CircleFrame,
  type LayoutItem,
} from '../lib/circleLayout'

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const data = (error as { response?: { data?: { error?: { message?: string }; message?: string } } }).response?.data
    return data?.error?.message || data?.message || 'La requête a échoué'
  }
  return error instanceof Error ? error.message : 'La requête a échoué'
}

export function HolacracyCanvas() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const teamFromUrl = params.get('team') ?? ''
  const circleFromUrl = params.get('circle') ?? ''
  const roleFromUrl = params.get('role') ?? ''
  const scope = 'template' as 'template' | 'instance'
  const [selectedId, setSelectedId] = useState(teamFromUrl)
  const [scale, setScale] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const [role, setRole] = useState<CanvasRole | null>(null)
  const [circleId, setCircleId] = useState<string | null>(circleFromUrl || null)
  const [draftName, setDraftName] = useState('')
  const [draftPurpose, setDraftPurpose] = useState('')
  const [draftDomains, setDraftDomains] = useState<string[]>([])
  const [draftAccountabilities, setDraftAccountabilities] = useState<string[]>([])
  const [draftOrchestrator, setDraftOrchestrator] = useState(false)
  const [draftRoleId, setDraftRoleId] = useState<string | null>(null)
  const [skills, setSkills] = useState<RoleSkillChoice[]>([])
  const [memberId, setMemberId] = useState('')
  const [chosenAgentId, setChosenAgentId] = useState('')
  const [declaredDraft, setDeclaredDraft] = useState<string | null>(null)
  const [notice, setNotice] = useState('')
  const [showResources, setShowResources] = useState(false)
  const [detailOpen, setDetailOpen] = useState(true)
  const [folded, setFolded] = useState<Record<string, boolean>>({})
  const [layoutPass, setLayoutPass] = useState(0)
  const [geometries, setGeometries] = useState<Record<string, CirclePlacement>>({})
  const geometriesRef = useRef<Record<string, CirclePlacement>>({})
  const boards = useRef(new Map<string, HTMLElement>())
  const boardFrame = useRef<HTMLDivElement>(null)
  const framesRef = useRef<CircleFrame[]>([])
  const dragToken = useRef(0)
  const [circleShift, setCircleShift] = useState<Record<string, { x: number; y: number }>>({})
  const spreadToken = useRef('')

  const catalog = useQuery('skill-catalog', () => circleService.catalog())
  const templates = useQuery('team-templates', () => teamService.getAllTeamTemplates())
  const agents = useQuery('agents', () => agentService.getAllAgents())
  const canvas = useQuery<CanvasView>(
    ['circles', scope, selectedId],
    () => circleService.canvas(scope, selectedId),
    { enabled: selectedId.length > 0, retry: false },
  )
  const catalogBindings = useQuery<CatalogBindingView>(
    ['catalog-bindings', 'template', selectedId],
    () => catalogService.bindings('template', selectedId),
    { enabled: selectedId.length > 0, retry: false },
  )
  const subjectId = chosenAgentId || memberId
  const declared = useQuery<string[]>(
    ['declared-skills', chosenAgentId ? 'agent' : 'human', subjectId],
    () => circleService.declaredSkills(chosenAgentId ? { agentId: subjectId } : { userId: subjectId }),
    { enabled: scope === 'instance' && subjectId.length > 0, retry: false },
  )
  const declaredText = declaredDraft ?? (declared.data ?? []).join(', ')
  const openCircle = (canvas.data?.circles ?? []).find((item) => item.id === circleId) ?? null
  const appliedFocus = useRef('')

  // * The address comes from the team list. It names the model, the circle, and sometimes the role.
  useEffect(() => {
    if (!teamFromUrl) {
      return
    }
    setSelectedId(teamFromUrl)
  }, [teamFromUrl])

  const expandedTeam = useRef('')
  useEffect(() => {
    if (circleFromUrl || expandedTeam.current === selectedId) {
      return
    }
    expandedTeam.current = selectedId
    setFolded({})
  }, [circleFromUrl, selectedId])

  useEffect(() => {
    if (!circleFromUrl || !canvas.data || canvas.data.id !== selectedId) {
      return
    }
    const key = `${selectedId}|${circleFromUrl}|${roleFromUrl}`
    if (appliedFocus.current === key) {
      return
    }
    const circle = canvas.data.circles.find((item) => item.id === circleFromUrl)
    if (!circle) {
      return
    }
    appliedFocus.current = key
    const next: Record<string, boolean> = {}
    for (const item of canvas.data.circles) {
      if (item.id !== circle.id) {
        next[item.id] = true
      }
    }
    setFolded(next)
    setLayoutPass((value) => value + 1)
    setOffset({ x: 48 - circle.x, y: 48 - circle.y })
    setScale(1)
    setCircleId(circle.id)
    setRole(roleFromUrl ? circle.roles.find((item) => item.id === roleFromUrl) ?? null : null)
    setDetailOpen(true)
  }, [canvas.data, circleFromUrl, roleFromUrl, selectedId])

  if ((role?.id ?? null) !== draftRoleId) {
    setDraftRoleId(role?.id ?? null)
    setDraftName(role?.name ?? '')
    setDraftPurpose(role?.purpose ?? '')
    setDraftDomains(role?.domains ?? [])
    setDraftAccountabilities(role?.accountabilities ?? [])
    setDraftOrchestrator(role?.isOrchestrator ?? false)
    setSkills(role?.skills ?? [])
  }

  async function run(action: () => Promise<void>) {
    setNotice('')
    try {
      await action()
      await canvas.refetch()
      if (selectedId) {
        await catalogBindings.refetch()
      }
      if (subjectId) {
        await declared.refetch()
      }
    } catch (error) {
      setNotice(errorMessage(error))
    }
  }

  function openNewRole(nextCircle: string) {
    setCircleId(nextCircle)
    setRole(null)
    setDetailOpen(true)
  }

  function openRole(nextCircle: string, nextRole: CanvasRole) {
    setCircleId(nextCircle)
    setRole(nextRole)
    setDetailOpen(true)
    setSkills(nextRole.skills)
    setDeclaredDraft(null)
    if (nextRole.assignee?.kind === 'agent') {
      setChosenAgentId(nextRole.assignee.id)
      setMemberId('')
    } else if (nextRole.assignee?.kind === 'human') {
      setMemberId(nextRole.assignee.id)
      setChosenAgentId('')
    } else {
      setChosenAgentId('')
      setMemberId('')
    }
  }

  function skillList(value: string) {
    return value.split(',').map((item) => item.trim()).filter((item) => item.length > 0)
  }

  function dragCircle(event: React.PointerEvent<HTMLElement>, item: CanvasCircle) {
    event.stopPropagation()
    event.preventDefault()
    const startX = event.clientX
    const startY = event.clientY
    const startFrames = framesRef.current.map((frame) => ({ ...frame }))
    const origin = startFrames.find((frame) => frame.id === item.id)
    if (!origin) {
      return
    }
    const token = dragToken.current + 1
    dragToken.current = token
    let moved = false
    let latest = startFrames
    const move = (ev: PointerEvent) => {
      const dx = (ev.clientX - startX) / scale
      const dy = (ev.clientY - startY) / scale
      if (!moved && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 3) {
        return
      }
      moved = true
      const dragged = startFrames.map((frame) => (
        frame.id === item.id ? { ...frame, x: origin.x + dx, y: origin.y + dy } : { ...frame }
      ))
      latest = separateCircles(dragged, item.id)
      setCircleShift(Object.fromEntries(latest.map((frame) => [frame.id, { x: frame.x, y: frame.y }])))
    }
    const up = () => {
      if (!moved) {
        return
      }
      const changed = latest.filter((frame) => {
        const before = startFrames.find((candidate) => candidate.id === frame.id)
        return !before || Math.abs(before.x - frame.x) > 0.5 || Math.abs(before.y - frame.y) > 0.5
      })
      void Promise.all(changed.map((frame) => circleService.updateCircle(frame.id, { x: Math.round(frame.x), y: Math.round(frame.y) })))
        .then(() => canvas.refetch())
        .catch((error: unknown) => setNotice(errorMessage(error)))
        .finally(() => {
          if (dragToken.current === token) {
            setCircleShift({})
          }
        })
    }
    trackPointer(event, move, up)
  }

  function beginPan(event: React.PointerEvent<HTMLElement>) {
    if (event.button !== 0 || event.target !== event.currentTarget) {
      return
    }
    const startX = event.clientX
    const startY = event.clientY
    const origin = { ...offset }
    const move = (ev: PointerEvent) => setOffset({ x: origin.x + ev.clientX - startX, y: origin.y + ev.clientY - startY })
    trackPointer(event, move, () => undefined)
  }

  useEffect(() => {
    const frame = boardFrame.current
    if (!frame) {
      return undefined
    }
    const blockScroll = (event: TouchEvent) => {
      if (event.cancelable) {
        event.preventDefault()
      }
    }
    frame.addEventListener('touchmove', blockScroll, { passive: false })
    return () => frame.removeEventListener('touchmove', blockScroll)
  }, [])

  useEffect(() => {
    const data = canvas.data
    if (!data) {
      return
    }
    const signature = data.circles.map((item) => `${item.id}:${Math.round(item.x)}:${Math.round(item.y)}`).join('|')
    if (spreadToken.current === signature) {
      return
    }
    const next = spreadCircleOrigins(data.circles)
    if (!next) {
      spreadToken.current = signature
      return
    }
    const planned = next.map((item) => `${item.id}:${Math.round(item.x)}:${Math.round(item.y)}`).join('|')
    if (spreadToken.current === planned) {
      return
    }
    spreadToken.current = planned
    void Promise.all(next.map((item) => circleService.updateCircle(item.id, { x: item.x, y: item.y })))
      .then(() => canvas.refetch())
      .catch((error: unknown) => setNotice(errorMessage(error)))
  }, [canvas.data, canvas.refetch])

  function rememberGeometry(id: string, placement: CirclePlacement) {
    const current = geometriesRef.current[id]
    if (current && samePlacement(current, placement)) {
      return
    }
    geometriesRef.current = { ...geometriesRef.current, [id]: placement }
    setGeometries(geometriesRef.current)
  }

  function registerBoard(id: string, element: HTMLElement | null) {
    if (element) {
      boards.current.set(id, element)
      return
    }
    boards.current.delete(id)
  }

  function circleAt(clientX: number, clientY: number): string | null {
    let found: string | null = null
    for (const circle of canvas.data?.circles ?? []) {
      const element = boards.current.get(circle.id)
      if (!element) {
        continue
      }
      const rect = element.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2
      if (Math.hypot(clientX - centerX, clientY - centerY) <= rect.width / 2) {
        found = circle.id
      }
    }
    return found
  }

  function resolvePort(clientX: number, clientY: number): PortHit | null {
    let best: { hit: PortHit; distance: number } | null = null
    for (const circle of canvas.data?.circles ?? []) {
      const element = boards.current.get(circle.id)
      const geo = geometriesRef.current[circle.id]
      if (!element || !geo || geo.radius === 0) {
        continue
      }
      const rect = element.getBoundingClientRect()
      const scale = rect.width / (geo.radius * 2)
      const tolerance = Math.max(16, 18 * scale)
      for (const item of geo.items) {
        if (item.kind !== 'role') {
          continue
        }
        const role = circle.roles.find((candidate) => candidate.id === item.id)
        if (!role) {
          continue
        }
        const ports: PortHit['port'][] = role.isLiaison
          ? ['in', 'out', 'external-in', 'external-out']
          : role.communicationInterne
            ? ['in', 'out']
            : []
        for (const port of ports) {
          const anchor = portAnchor(item, port)
          const screenX = rect.left + (geo.radius + anchor.x) * scale
          const screenY = rect.top + (geo.radius + anchor.y) * scale
          const distance = Math.hypot(clientX - screenX, clientY - screenY)
          if (distance > tolerance) {
            continue
          }
          if (!best || distance < best.distance) {
            best = { hit: { circleId: circle.id, roleId: role.id, port }, distance }
          }
        }
      }
    }
    return best?.hit ?? null
  }

  function connectPorts(from: PortHit, to: PortHit | null) {
    if (!to || to.roleId === from.roleId) {
      return
    }
    const forward = isOutput(from.port) && isInput(to.port)
    const backward = isInput(from.port) && isOutput(to.port)
    if (!forward && !backward) {
      return
    }
    const source = forward ? from : to
    const target = forward ? to : from
    if (source.circleId === target.circleId) {
      if (isExternal(source.port) || isExternal(target.port)) {
        return
      }
      void run(() => circleService.toggleLink(source.circleId, source.roleId, target.roleId))
      return
    }
    void run(() => circleService.ensureBridge(source.roleId, target.roleId))
  }

  function localInCircle(circleId: string, clientX: number, clientY: number): { x: number; y: number } | null {
    const element = boards.current.get(circleId)
    const geo = geometriesRef.current[circleId]
    if (!element || !geo || geo.radius === 0) {
      return null
    }
    const rect = element.getBoundingClientRect()
    const scale = rect.width / (geo.radius * 2)
    if (scale === 0) {
      return null
    }
    return {
      x: (clientX - rect.left) / scale - geo.radius,
      y: (clientY - rect.top) / scale - geo.radius,
    }
  }

  function focusCircle(nextCircleId: string, nextRoleId = '') {
    const circles = canvas.data?.circles ?? []
    const next: Record<string, boolean> = {}
    for (const item of circles) {
      if (item.id !== nextCircleId) {
        next[item.id] = true
      }
    }
    appliedFocus.current = `${selectedId}|${nextCircleId}|${nextRoleId}`
    expandedTeam.current = selectedId
    setFolded(next)
    setLayoutPass((value) => value + 1)
    setCircleId(nextCircleId)
    const found = circles.find((item) => item.id === nextCircleId) ?? null
    setRole(nextRoleId && found ? found.roles.find((item) => item.id === nextRoleId) ?? null : null)
    setDetailOpen(true)
    if (found) {
      setOffset({ x: 48 - found.x, y: 48 - found.y })
    }
    navigate(circleModelPath(selectedId, nextCircleId, nextRoleId || undefined))
  }

  function foldEvery(collapsed: boolean) {
    const next: Record<string, boolean> = {}
    if (collapsed) {
      for (const item of canvas.data?.circles ?? []) {
        next[item.id] = true
      }
    }
    setFolded(next)
    setLayoutPass((value) => value + 1)
  }

  function fitCircles() {
    const frame = boardFrame.current
    if (!frame || boardCircles.length === 0) {
      return
    }
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const circle of boardCircles) {
      const radius = geometries[circle.id]?.radius ?? circleRadius(Math.max(1, circle.roles.filter((item) => !item.isLiaison).length))
      minX = Math.min(minX, circle.x)
      minY = Math.min(minY, circle.y)
      maxX = Math.max(maxX, circle.x + radius * 2)
      maxY = Math.max(maxY, circle.y + radius * 2)
    }
    const width = Math.max(1, maxX - minX)
    const height = Math.max(1, maxY - minY)
    const pad = 36
    const nextScale = Math.min((frame.clientWidth - pad * 2) / width, (frame.clientHeight - pad * 2) / height)
    const fitted = Math.min(2, Math.max(0.15, nextScale))
    setScale(fitted)
    setOffset({
      x: (frame.clientWidth - width * fitted) / 2 - minX * fitted,
      y: (frame.clientHeight - height * fitted) / 2 - minY * fitted,
    })
  }

  const choices = templates.data ?? []
  const boardCircles = canvas.data?.circles ?? []
  const shownCircles = boardCircles.map((circle) => {
    const shift = circleShift[circle.id]
    return shift ? { ...circle, x: shift.x, y: shift.y } : circle
  })
  framesRef.current = shownCircles.map((circle) => ({
    id: circle.id,
    x: circle.x,
    y: circle.y,
    radius: geometries[circle.id]?.radius ?? circleRadius(Math.max(1, circle.roles.filter((role) => !role.isLiaison).length)),
  }))
  const world = shownCircles.reduce((box, circle) => {
    const radius = geometries[circle.id]?.radius ?? circleRadius(Math.max(1, circle.roles.filter((role) => !role.isLiaison).length))
    return {
      width: Math.max(box.width, circle.x + radius * 2 + 180),
      height: Math.max(box.height, circle.y + radius * 2 + 180),
    }
  }, { width: 1600, height: 1000 })
  const bridges = shownCircles.flatMap((circle) => (circle.links ?? []).filter((link) => link.kind === 'external'))
  const bridgeItems = worldRoleItems(shownCircles, geometries)
  const resourceQueries = useQueries(
    boardCircles.map((item) => ({
      queryKey: ['circle-board-resources', item.id],
      queryFn: () => circleService.resources(item.id),
      enabled: showResources,
    })),
  )

  return (
    <div className="page-inset p-6 space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-secondary block" htmlFor="circle-team">
          Équipe
          <select
            id="circle-team"
            name="circle-team"
            className="form-input mt-1"
            value={selectedId}
            onChange={(event) => {
              const nextTeam = event.target.value
              appliedFocus.current = ''
              expandedTeam.current = nextTeam
              setFolded({})
              setSelectedId(nextTeam)
              setCircleId(null)
              setRole(null)
              navigate(nextTeam ? `/teams/circles?team=${nextTeam}` : '/teams/circles')
            }}
          >
            <option value="">Choisir</option>
            {choices.map((item) => (
              <option key={item.id} value={item.id}>{item.name || 'Sans nom'}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="button button--secondary"
          disabled={selectedId.length === 0}
          onClick={() => void run(async () => {
            await circleService.createCircle({ templateId: selectedId, purpose: 'Cercle', kind: 'agent' })
          })}
        >
          Ajouter un cercle
        </button>
        </div>
        {selectedId && canvas.data ? (
          <CircleRoleSummary
            teamId={selectedId}
            circles={canvas.data.circles.map((item) => ({
              id: item.id,
              purpose: item.purpose,
              roles: item.roles.map((itemRole) => ({ id: itemRole.id, name: itemRole.name })),
            }))}
            onOpen={focusCircle}
          />
        ) : null}
      </div>

      {notice ? <p className="text-sm text-error">{notice}</p> : null}
      {canvas.isLoading ? <p className="text-sm text-secondary">Chargement…</p> : null}
      {canvas.error ? <p className="text-sm text-error">{errorMessage(canvas.error)}</p> : null}

      <div className="canvas-layout flex gap-4 items-start">
        <div
          ref={boardFrame}
          className="canvas-board relative flex-1 min-w-0 overflow-hidden rounded-xl bg-gray-100 border border-gray-200"
          style={{ height: 640 }}
        >
          <div
            className="absolute inset-0"
            style={{ cursor: 'grab' }}
            onPointerDown={beginPan}
          >
          <div
            onPointerDown={beginPan}
            style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transformOrigin: '0 0', width: world.width, height: world.height, position: 'relative', cursor: 'grab' }}
          >
            {shownCircles.map((item, index) => (
              <CircleBoard
                key={item.id}
                circle={item}
                documents={circleDocuments(item, resourceQueries[index]?.data ?? [], catalogBindings.data)}
                showDocuments={showResources}
                selectedRoleId={circleId === item.id ? role?.id ?? null : null}
                open={circleId === item.id}
                onOpenRole={(itemRole) => openRole(item.id, itemRole)}
                onOpenCircle={() => openNewRole(item.id)}
                onDragCircle={(event) => dragCircle(event, item)}
                onSaveLayout={(positions) => {
                  void circleService.saveLayout(item.id, positions)
                }}
                onRemoveLink={(linkId) => {
                  void run(() => circleService.removeLink(linkId))
                }}
                onRegister={(element) => registerBoard(item.id, element)}
                onGeometry={rememberGeometry}
                resolvePort={resolvePort}
                onConnect={connectPorts}
                circleAt={circleAt}
                folded={folded[item.id] === true}
                layoutPass={layoutPass}
                onToggleFold={() => {
                  setFolded((current) => ({ ...current, [item.id]: current[item.id] !== true }))
                  setLayoutPass((value) => value + 1)
                }}
                onMoveRole={(roleId, targetId, clientX, clientY) => {
                  const point = localInCircle(targetId, clientX, clientY)
                  setNotice('')
                  return circleService.moveRole(roleId, targetId, point ?? undefined)
                    .then(async () => {
                      await canvas.refetch()
                    })
                    .catch((error: unknown) => {
                      setNotice(errorMessage(error))
                      throw error
                    })
                }}
              />
            ))}
            <svg
              width={world.width}
              height={world.height}
              style={{ position: 'absolute', inset: 0, zIndex: 4, overflow: 'visible', pointerEvents: 'none' }}
            >
              {bridges.map((link) => {
                const ends = bridgeEnds(shownCircles, geometries, link.sourceRoleId, link.targetRoleId)
                if (!ends) {
                  return null
                }
                const dotted = linkCrossesRole(ends.from.x, ends.from.y, ends.to.x, ends.to.y, bridgeItems, link.sourceRoleId, link.targetRoleId)
                return (
                  <LinkShape
                    key={link.id}
                    x1={ends.from.x}
                    y1={ends.from.y}
                    x2={ends.to.x}
                    y2={ends.to.y}
                    dotted={dotted}
                    external
                    onRemove={() => {
                      void run(() => circleService.removeLink(link.id))
                    }}
                  />
                )
              })}
            </svg>
          </div>
          </div>
          <div className="absolute flex flex-wrap gap-2" style={{ top: 12, left: 12, right: 12, zIndex: 8 }}>
            <div className="flex items-center gap-1" role="group" aria-label="Zoom" style={canvasToolGroupStyle()}>
              <span className="text-xs text-secondary">Zoom</span>
              <button type="button" className="button button--secondary" title="Éloigner" aria-label="Éloigner" onClick={() => setScale((value) => Math.max(0.15, value - 0.1))} style={canvasZoomStyle()}>
                <Minus className="w-4 h-4" />
              </button>
              <button type="button" className="button button--secondary" title="Rapprocher" aria-label="Rapprocher" onClick={() => setScale((value) => Math.min(2, value + 0.1))} style={canvasZoomStyle()}>
                <Plus className="w-4 h-4" />
              </button>
              <button type="button" className="button button--secondary" title="Ajuster tous les cercles" aria-label="Ajuster tous les cercles" onClick={fitCircles} disabled={boardCircles.length === 0} style={canvasZoomStyle()}>
                <Scan className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-center gap-1" role="group" aria-label="Pliage" style={canvasToolGroupStyle()}>
              <span className="text-xs text-secondary">Plier</span>
              <button type="button" className="button button--secondary" title="Tout replier" aria-label="Tout replier" onClick={() => foldEvery(true)} style={canvasFoldStyle()}>
                <ChevronsDownUp className="w-4 h-4" />
              </button>
              <button type="button" className="button button--secondary" title="Tout déplier" aria-label="Tout déplier" onClick={() => foldEvery(false)} style={canvasFoldStyle()}>
                <ChevronsUpDown className="w-4 h-4" />
              </button>
            </div>
          </div>
          <label className="text-sm text-secondary flex items-center gap-2" htmlFor="circle-show-resources" style={canvasResourceStyle()}>
            <input
              id="circle-show-resources"
              name="circle-show-resources"
              type="checkbox"
              checked={showResources}
              onChange={(event) => setShowResources(event.target.checked)}
            />
            Afficher les ressources
          </label>
        </div>

        <aside
          className={`canvas-detail card p-4 ${detailOpen ? 'space-y-3 overflow-y-auto' : ''}`}
          style={detailOpen ? { maxHeight: 640 } : undefined}
        >
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-primary">
              {detailOpen && role ? 'Modifier le rôle' : detailOpen && circleId ? 'Nouveau rôle' : 'Fiche du rôle'}
            </h2>
            <button
              type="button"
              className="button button--secondary flex-shrink-0"
              aria-expanded={detailOpen}
              onClick={() => setDetailOpen((open) => !open)}
            >
              {detailOpen ? 'Replier' : 'Déplier'}
            </button>
          </div>
          {detailOpen && circleId ? (
            <>
            <p className="text-sm text-secondary">
              Cercle : {openCircle?.purpose || 'Cercle'}
              {openCircle ? ` · ${openCircle.kind === 'human' ? 'Humains' : 'Agents'}` : ''}
            </p>
            <label className="text-sm text-secondary block" htmlFor="role-draft-name">
              Nom
              <input
                id="role-draft-name"
                name="role-draft-name"
                className="form-input mt-1"
                value={draftName}
                onChange={(event) => setDraftName(event.target.value)}
                required
              />
            </label>
            <label className="text-sm text-secondary block" htmlFor="role-draft-purpose">
              Raison d'être
              <textarea
                id="role-draft-purpose"
                name="role-draft-purpose"
                className="form-input mt-1"
                rows={3}
                value={draftPurpose}
                onChange={(event) => setDraftPurpose(event.target.value)}
                placeholder="Capacité ou but que ce rôle poursuit"
                required
              />
            </label>
            <p className="text-xs text-secondary">Obligatoire. Capacité, potentiel ou but que ce rôle poursuit au nom de l'organisation.</p>
            <CharterLines
              idPrefix="role-domain"
              label="Domaines"
              hint="Choses que ce rôle est le seul à contrôler, au nom de l'organisation."
              lines={draftDomains}
              onChange={setDraftDomains}
            />
            <CharterLines
              idPrefix="role-accountability"
              label="Redevabilités"
              hint="Activités dans la durée que ce rôle met en œuvre."
              lines={draftAccountabilities}
              onChange={setDraftAccountabilities}
            />
            <p className="text-xs text-secondary">Un membre peut tenir plusieurs rôles.</p>
            <div>
              <p className="text-sm text-secondary mb-2">Compétences exigées</p>
              <SkillPicker catalog={catalog.data ?? []} value={skills} onChange={setSkills} />
            </div>
            {role && (role.isLiaison || role.communicationExterne) ? (
              <p className="text-xs text-secondary">Ce rôle demande la compétence « communication externe ».</p>
            ) : null}
            <label className="text-sm text-secondary flex items-center gap-2" htmlFor="circle-orchestrator">
              <input
                id="circle-orchestrator"
                name="circle-orchestrator"
                type="checkbox"
                checked={draftOrchestrator}
                onChange={(event) => setDraftOrchestrator(event.target.checked)}
              />
              Orchestrateur
            </label>
            {role && scope === 'instance' && selectedId ? (
              <div className="space-y-2">
                {canvas.data?.circles.find((item) => item.id === circleId)?.kind === 'human' ? (
                  <label className="text-sm text-secondary block">
                    Compte
                    <input id="circle-member" name="circle-member"
                      className="form-input mt-1"
                      value={memberId}
                      onChange={(event) => { setMemberId(event.target.value); setChosenAgentId(''); setDeclaredDraft(null) }}
                      placeholder="Identifiant du compte"
                    />
                  </label>
                ) : (
                  <label className="text-sm text-secondary block">
                    Membre
                    <select id="circle-agent" name="circle-agent"
                      className="form-input mt-1"
                      value={chosenAgentId}
                      onChange={(event) => { setChosenAgentId(event.target.value); setMemberId(''); setDeclaredDraft(null) }}
                    >
                      <option value="">Choisir</option>
                      {(agents.data ?? []).map((agent) => (
                        <option key={agent.id} value={agent.id}>{agent.name}</option>
                      ))}
                    </select>
                  </label>
                )}
                <label className="text-sm text-secondary block">
                  Compétences déclarées
                  <input id="circle-declared-skills" name="circle-declared-skills"
                    className="form-input mt-1"
                    value={declaredText}
                    onChange={(event) => setDeclaredDraft(event.target.value)}
                    placeholder="communication externe"
                  />
                </label>
                <button
                  type="button"
                  className="button button--secondary"
                  disabled={subjectId.length === 0}
                  onClick={() => void run(async () => {
                    const list = skillList(declaredText)
                    if (chosenAgentId) {
                      await circleService.saveSkills({ agentId: chosenAgentId, skills: list })
                      await circleService.assign(role.id, { teamInstanceId: selectedId, agentId: chosenAgentId })
                    } else {
                      await circleService.saveSkills({ userId: memberId.trim(), skills: list })
                      await circleService.assign(role.id, { teamInstanceId: selectedId, userId: memberId.trim() })
                    }
                    setDeclaredDraft(null)
                  })}
                >
                  Déclarer et assigner
                </button>
              </div>
            ) : null}
            {role && circleId ? (
              <RoleCatalogResources teamId={selectedId} circleId={circleId} roleId={role.id} />
            ) : null}
            <div className="flex gap-2">
              <button
                type="button"
                className="button button--primary"
                disabled={!charterReady(draftName, draftPurpose)}
                onClick={() => void run(async () => {
                  if (!circleId || !charterReady(draftName, draftPurpose)) {
                    return
                  }
                  const domains = filledLines(draftDomains)
                  const accountabilities = filledLines(draftAccountabilities)
                  if (role) {
                    await circleService.updateRole(role.id, {
                      name: draftName.trim(),
                      purpose: draftPurpose,
                      domains,
                      accountabilities,
                      skills,
                      isOrchestrator: draftOrchestrator,
                    })
                    setRole({
                      ...role,
                      name: draftName.trim(),
                      purpose: draftPurpose,
                      domains,
                      accountabilities,
                      skills,
                      isOrchestrator: draftOrchestrator,
                    })
                    return
                  }
                  await circleService.createRole(circleId, {
                    name: draftName.trim(),
                    purpose: draftPurpose,
                    domains,
                    accountabilities,
                    skills,
                    isOrchestrator: draftOrchestrator,
                  })
                  setDraftName('')
                  setDraftPurpose('')
                  setDraftDomains([])
                  setDraftAccountabilities([])
                  setDraftOrchestrator(false)
                  setSkills([])
                })}
              >
                Enregistrer
              </button>
              {role && !role.isLiaison ? (
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => {
                    if (!window.confirm('Supprimer ce rôle ?')) {
                      return
                    }
                    void run(async () => {
                      await circleService.deleteRole(role.id)
                      setRole(null)
                    })
                  }}
                >
                  Supprimer
                </button>
              ) : null}
            </div>
            </>
          ) : detailOpen ? (
            <p className="text-sm text-secondary">Cliquez un cercle pour ajouter un rôle, ou un rôle pour le modifier.</p>
          ) : null}
        </aside>
      </div>
      <div className="text-sm text-secondary space-y-1">
        <p className="font-medium text-primary">Cercles</p>
        <p>Choisissez une équipe. Un clic sur un cercle l'ouvre et replie les autres.</p>
        <p>Tous les cercles sont visibles ensemble. Glissez un rôle dans un autre cercle pour l'y placer. Un lien vers un autre cercle passe par les rôles de liaison.</p>
      </div>
    </div>
  )
}

function circleDocuments(
  circle: CanvasCircle,
  legacy: Array<{ id: string; name: string; kind: string }>,
  view: CatalogBindingView | undefined,
): Array<{ id: string; name: string }> {
  const cards = legacy
    .filter((resource) => resource.kind === 'document')
    .map((resource) => ({ id: resource.id, name: resource.name }))
  const seen = new Set(cards.map((card) => card.id))
  const roleIds = new Set(circle.roles.map((item) => item.id))
  for (const grant of view?.grants ?? []) {
    const covers = grant.scope === 'global'
      || (grant.scope === 'circle' && grant.circleId === circle.id)
      || (grant.scope === 'role' && grant.roleId !== null && roleIds.has(grant.roleId))
    if (!covers || seen.has(grant.resourceId)) {
      continue
    }
    seen.add(grant.resourceId)
    cards.push({ id: grant.resourceId, name: grant.name })
  }
  return cards
}

function canvasZoomStyle(): CSSProperties {
  return {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: '50%',
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}

function canvasFoldStyle(): CSSProperties {
  return {
    width: 32,
    height: 32,
    minWidth: 32,
    borderRadius: 6,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }
}

function canvasToolGroupStyle(): CSSProperties {
  return {
    padding: '4px 8px',
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid #d6d3d1',
  }
}

function canvasResourceStyle(): CSSProperties {
  return {
    position: 'absolute',
    left: 12,
    bottom: 12,
    zIndex: 8,
    padding: '6px 10px',
    borderRadius: 10,
    background: 'rgba(255, 255, 255, 0.92)',
    border: '1px solid #d6d3d1',
  }
}

function samePlacement(left: CirclePlacement, right: CirclePlacement): boolean {
  if (left.radius !== right.radius || left.items.length !== right.items.length) {
    return false
  }
  return left.items.every((item, index) => {
    const other = right.items[index]
    return Boolean(other && item.id === other.id && item.x === other.x && item.y === other.y && item.ring === other.ring)
  })
}

function worldRoleItems(circles: CanvasCircle[], geometries: Record<string, CirclePlacement>): LayoutItem[] {
  const items: LayoutItem[] = []
  for (const circle of circles) {
    const geo = geometries[circle.id]
    if (!geo) {
      continue
    }
    for (const item of geo.items) {
      if (item.kind !== 'role') {
        continue
      }
      items.push({
        ...item,
        x: circle.x + geo.radius + item.x,
        y: circle.y + geo.radius + item.y,
      })
    }
  }
  return items
}

function bridgeEnds(
  circles: CanvasCircle[],
  geometries: Record<string, CirclePlacement>,
  sourceRoleId: string,
  targetRoleId: string,
): { from: { x: number; y: number }; to: { x: number; y: number } } | null {
  const from = rolePortOnBoard(circles, geometries, sourceRoleId, 'external-out')
  const to = rolePortOnBoard(circles, geometries, targetRoleId, 'external-in')
  if (!from || !to) {
    return null
  }
  return { from, to }
}

function rolePortOnBoard(
  circles: CanvasCircle[],
  geometries: Record<string, CirclePlacement>,
  roleId: string,
  port: 'external-in' | 'external-out',
): { x: number; y: number } | null {
  for (const circle of circles) {
    const geo = geometries[circle.id]
    const item = geo?.items.find((candidate) => candidate.id === roleId)
    if (!geo || !item) {
      continue
    }
    const anchor = portAnchor(item, port)
    return {
      x: circle.x + geo.radius + anchor.x,
      y: circle.y + geo.radius + anchor.y,
    }
  }
  return null
}

function filledLines(lines: string[]): string[] {
  const seen = new Set<string>()
  const filled: string[] = []
  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed || seen.has(trimmed)) {
      continue
    }
    seen.add(trimmed)
    filled.push(trimmed)
  }
  return filled
}

function charterReady(name: string, purpose: string): boolean {
  return name.trim() !== '' && purpose.trim() !== ''
}

/**
 * * Editable list of domains or accountabilities. A blank row stays available.
 */
function CharterLines({
  idPrefix,
  label,
  hint,
  lines,
  onChange,
}: {
  idPrefix: string
  label: string
  hint: string
  lines: string[]
  onChange: (lines: string[]) => void
}) {
  const shown = lines.length === 0 ? [''] : lines
  return (
    <div className="space-y-1">
      <p className="text-sm text-secondary">{label}</p>
      <p className="text-xs text-secondary">{hint}</p>
      {shown.map((line, index) => (
        <div key={`${idPrefix}-${index}`} className="flex gap-2">
          <input
            id={`${idPrefix}-${index}`}
            name={`${idPrefix}-${index}`}
            className="form-input"
            value={line}
            onChange={(event) => {
              const next = [...shown]
              next[index] = event.target.value
              onChange(next)
            }}
          />
          {shown.length > 1 ? (
            <button
              type="button"
              className="button button--secondary"
              onClick={() => onChange(shown.filter((_, item) => item !== index))}
            >
              Retirer
            </button>
          ) : null}
        </div>
      ))}
      <button type="button" className="button button--secondary" onClick={() => onChange([...shown, ''])}>
        Ajouter
      </button>
    </div>
  )
}
